import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import {
  HomeOutlined,
  DescriptionOutlined,
  StorefrontOutlined,
  AppsOutlined
} from '@mui/icons-material';

import Sidebar from './Sidebar';
import FormCircularButton from '../components/common/stories/FormCircularButton';
import { useSelectedLocation } from '../hooks/useSelectedLocation';
import { useAppState } from '../hooks/useAppState';

const ProtectedLayout = ({ children }) => {
  const [isMenuOpen, setMenuOpen] = useState(false);
  const [isCheckingLocation, setIsCheckingLocation] = useState(true);
  const [locationCheckAttempts, setLocationCheckAttempts] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();

  // Use localStorage-based hook for location (synced with Sidebar)
  const selectedLocation = useSelectedLocation();
  // Use centralized state for organizationId
  const { organizationId, hasSetupLocation } = useAppState();

  // Check if location is set up - give time for Sidebar to fetch and set location
  useEffect(() => {
    // If we have a valid location, stop checking immediately
    if (selectedLocation) {
      setIsCheckingLocation(false);
      return;
    }

    // Wait for Sidebar to fetch and set location from API
    // Check every 200ms up to 10 times (2 seconds total)
    if (locationCheckAttempts < 10) {
      const timer = setTimeout(() => {
        setLocationCheckAttempts((prev) => prev + 1);
      }, 200);
      return () => clearTimeout(timer);
    }

    // After max attempts, stop checking
    setIsCheckingLocation(false);
  }, [selectedLocation, locationCheckAttempts]);

  useEffect(() => {
    // Only redirect if:
    // 1. We're done checking
    // 2. No selectedLocation in localStorage
    // 3. API confirms no location setup (hasSetupLocation is false, not undefined/null)
    if (!isCheckingLocation && !selectedLocation && hasSetupLocation === false) {
      navigate('/welcome', {
        state: { message: 'Please set up at least one store location to continue.' },
      });
    }
  }, [isCheckingLocation, selectedLocation, hasSetupLocation, navigate]);

  // Determine current navigation section
  let currentNav;
  if (location.pathname.startsWith('/dashboard')) {
    currentNav = 'DASHBOARD';
  } else if (
    location.pathname.startsWith('/products') ||
    location.pathname.startsWith('/addproduct') ||
    location.pathname.startsWith('/bulkuploadproducts') ||
    location.pathname.startsWith('/masterproducts')
  ) {
    currentNav = 'PRODUCTS';
  } else if (
    location.pathname.startsWith('/flyers') ||
    location.pathname.startsWith('/addflyer') ||
    location.pathname.startsWith('/flyertemplatemanager') ||
    location.pathname.startsWith('/flyertemplates') ||
    location.pathname.startsWith('/adtemplatemanager') ||
    location.pathname.startsWith('/adtemplates')
  ) {
    currentNav = 'FLYERS';
  } else if (
    location.pathname.startsWith('/flyer-analysis') ||
    location.pathname === '/settings/organizations' ||
    location.pathname.startsWith('/settings/organizations/') ||
    location.pathname === '/settings/universal-products' ||
    location.pathname === '/settings/admin-tokens'
  ) {
    currentNav = 'ADMIN';
  } else if (location.pathname.startsWith('/website')) {
    currentNav = 'WEBSITE';
  } else if (location.pathname.startsWith('/settings')) {
    // /settings/organization (singular) = Settings context
    // /settings/organizations (plural) = Admin context (matched above)
    currentNav = 'SETTINGS';
  }

  // Determine if we're still loading/waiting for location
  const isLoading = isCheckingLocation || (!selectedLocation && hasSetupLocation !== false);

  // Don't render if no location and we know setup is needed (will redirect)
  if (!selectedLocation && hasSetupLocation === false) {
    return null;
  }

  return (
    <div className={`protected-layout ${isMenuOpen ? 'menu-open' : ''}`}>
      {!isMenuOpen && (
        <div className="mobile-top-nav-logo">
          <img src="/logo-no-background.svg" alt="" className="mobile-top-nav-logo-image" />
        </div>
      )}
      <div className="mobile-logged-in-menu">
        <div className="mobile-menu-icon" onClick={() => setMenuOpen(!isMenuOpen)}>
          {isMenuOpen ? <span style={{ color: 'white' }}>&#10005;</span> : <span>&#9776;</span>}
        </div>
      </div>
      {/* Always render Sidebar so it can fetch and set locations */}
      <div className="left-column">
        <Sidebar navItems={currentNav} userOrganizationId={organizationId} />
      </div>
      {/* Show loading or content based on state */}
      <div className="main-content">
        {isLoading ? (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
            }}
          >
            <div>Loading...</div>
          </div>
        ) : (
          children
        )}
      </div>
      <div className="mobile-footer-menu">
        <div className="mobile-footer-menu-option">
          <Link to="/dashboard">
            <HomeOutlined />
            Home
          </Link>
        </div>
        <div className="mobile-footer-menu-option">
          <Link to="/flyers">
            <DescriptionOutlined />
            Flyers
          </Link>
        </div>
        <div className="mobile-footer-menu-option">
          <Link to="/addflyer">
            <FormCircularButton>+</FormCircularButton>
          </Link>
        </div>
        <div className="mobile-footer-menu-option">
          <Link to="/products">
            <StorefrontOutlined />
            Products
          </Link>
        </div>
        <div className="mobile-footer-menu-option">
          <Link to="/flyertemplates">
            <AppsOutlined />
            Templates
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ProtectedLayout;
